import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { IBreadcrumb } from '@app/lib/models/breadcrumb.model';
import { BreadcrumbComponent } from "@app/lib/components/breadcrumb/breadcrumb.component";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOption } from "@angular/material/core";
import { CommonModule } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { JudgePanelAssignmentService } from '../services/judge-panel-assignment.service';
import { IResponse } from '@app/lib/models/response.model';
import { ICourtSessionPanel, IJudge, IJusticePanelCount, ISessionData } from '../models/case-assignment.model';
import { ReplaySubject, Subject, takeUntil } from 'rxjs';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { NgFor } from '@angular/common';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { MatButtonModule } from '@angular/material/button';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDropListGroup, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { MatIcon } from "@angular/material/icon";
import { DialogService } from '@app/lib/services/dialog.service';



@Component({
  selector: 'app-judge-panel-assignment',
  standalone: true,
  imports: [BreadcrumbComponent,
    FormsModule,
    MatFormFieldModule,
    MatOption,
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    NgxMatSelectSearchModule,
    MatButtonModule,
    CdkDropListGroup,
    CdkDropList,
    CdkDrag,
    NgFor, MatIcon],
  templateUrl: './judge-panel-assignment.component.html',
  styleUrl: './judge-panel-assignment.component.scss'
})
export class JudgePanelAssignmentComponent implements OnInit {


  constructor(
    private dialog: MatDialog,
    private service: JudgePanelAssignmentService,
    private dialogService: DialogService,
  ) {

  }
  sessions: ISessionData[] = [];
  panels: ICourtSessionPanel[] = [];
  public filteredSessions: ReplaySubject<ISessionData[]> = new ReplaySubject<ISessionData[]>(1);
  public sessionFilterCtrl: FormControl<string | null> = new FormControl<string | null>('');
  public filteredPanels: ReplaySubject<ICourtSessionPanel[]> = new ReplaySubject<ICourtSessionPanel[]>(1);
  public panelsFilterCtrl: FormControl<string | null> = new FormControl<string | null>('');
  protected _onDestroy = new Subject<void>();

  public breadcrumbs: WritableSignal<IBreadcrumb[]> = signal([
    { label: 'Court' },
    { label: 'Judge Panel Assignment' },
  ]);
  public searchFormGrp: FormGroup = new FormGroup({
    sessionControl: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    panelControl: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  loading: boolean = false;
  isLoadingSession: boolean = false;
  isLoadingPanel: boolean = false;
  isSearching: boolean = false;
  isSearchComplete: boolean = false;

  // Modified Mock data for Judges Dual Listbox
  panelId: number = 0;
  panelName: string = '';
  availableJudges: IJudge[] = [];

  panelJudges: IJudge[] = [];

  panelCount: number = 0;
  justicePanels: IJusticePanelCount[] = []

  // State Tracking
  hasChanges: boolean = false;
  savedPanelJudges: IJudge[] = []; // To track "U" vs "I" records
  filteredAvailableJudges: IJudge[] = [];
  searchAvailableJudgesCtrl: FormControl = new FormControl('');

  drop(event: CdkDragDrop<IJudge[]>) {
    // Prevent adding more than 3 judges to the panel judges list
    if (event.previousContainer !== event.container && event.container.id === 'panelList') {
      if (this.panelJudges.length >= this.panelCount) {
        this.dialogService.openDialog1({
          data: {
            title: "Information",
            message: `Maximum of ${this.panelCount} judges allowed on the Panel!`,
            icon: 'info',
          }, disableClose: true,
          hasBackdrop: true,
          backdropClass: 'dialog-backdrop',
        });
        return; // Cancel transfer
      }
    }

    if (event.previousContainer === event.container) {
      if (event.previousIndex !== event.currentIndex) {
        moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        this.hasChanges = true;
      }
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );

      // Keep availableJudges in sync if we drag from/to filteredAvailableJudges
      if (event.previousContainer.id === 'availableList') {
        const itemMoved = event.container.data[event.currentIndex];
        this.availableJudges = this.availableJudges.filter(j => j.justiceId !== itemMoved.justiceId);
      } else if (event.container.id === 'availableList') {
        const itemMoved = event.container.data[event.currentIndex];
        if (!this.availableJudges.find(j => j.justiceId === itemMoved.justiceId)) {
          this.availableJudges.push(itemMoved);
        }
      }

      this.hasChanges = true;
    }
  }

  ngOnInit() {

    // Call the API to fetch courts
    this.getsession();

    // Listen for search changes
    this.sessionFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterSession();
      });
    this.panelsFilterCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(() => {
        this.filterPanels();
      });

    this.searchAvailableJudgesCtrl.valueChanges
      .pipe(takeUntil(this._onDestroy))
      .subscribe(val => {
        this.filterAvailableJudges(val);
      });
  }
  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }


  public onSessionChange(selected: any): void {
    if (!selected) return;
    const params = { 'id': selected.id }
    this.searchFormGrp.get('panelControl')?.patchValue('');
    this.isLoadingPanel = true;
    this.service.getpanels(params).subscribe({
      next: (res: IResponse<ICourtSessionPanel[]>) => {
        if (res && Array.isArray(res.success)) {
          // 1. Assign the data to the master list
          this.panels = res.success;

          if (!this.panels || this.panels.length === 0) {
            this.dialogService.openDialog1({
              data: {
                title: "Information",
                message: 'Session/Panel may not be active with current date. Please verify in Session Details to continue',
                icon: 'info',
              }, disableClose: true,
              hasBackdrop: true,
              backdropClass: 'dialog-backdrop',
            });
            // Emits an empty array, resetting any rendered dropdown options
            this.filteredPanels.next([]);

          } else {
            this.filteredPanels.next(this.panels.slice());

            this.filteredPanels.subscribe((panelsList) => {
              if (panelsList && panelsList.length === 1) {
                const singlePanel = panelsList[0];

                // Auto-select if not already selected
                if (this.searchFormGrp.get('panelControl')?.value !== singlePanel) {
                  this.searchFormGrp.get('panelControl')?.patchValue(singlePanel);
                  this.onSessionChange(singlePanel);
                }
              }
            });
          }
        } else {
          //console.log('No court data found.');
        }
        this.isLoadingPanel = false;
      },
      error: (err) => {
        console.error('Error fetching court data:', err);
        this.isLoadingPanel = false;
      },
    });
  }
  public getsession() {
    this.isLoadingSession = true;
    this.service.getSession().subscribe({
      next: (res: IResponse<ISessionData[]>) => {
        if (res && Array.isArray(res.success)) {
          // 1. Assign the data to the master list
          this.sessions = res.success;

          // 2. Initialize the filtered list for the dropdown
          this.filteredSessions.next(this.sessions.slice());

          this.filteredSessions.subscribe((sessionList) => {
            if (sessionList && sessionList.length === 1) {
              const singleSession = sessionList[0];

              // Auto-select if not already selected
              if (this.searchFormGrp.get('sessionControl')?.value !== singleSession) {
                this.searchFormGrp.get('sessionControl')?.patchValue(singleSession);
                this.onSessionChange(singleSession);
              }
            }
          });

          //console.log('Courts loaded successfully:', this.sessions);
        } else {
          //console.log('No court data found.');
        }
        this.isLoadingSession = false;
      },
      error: (err) => {
        console.error('Error fetching court data:', err);
        this.isLoadingSession = false;
      },
    });
  }

  public searchHandler() {
    this.availableJudges = [];
    this.filteredAvailableJudges = [];
    this.searchAvailableJudgesCtrl.setValue('');
    this.panelJudges = [];
    this.savedPanelJudges = [];
    this.hasChanges = false;
    this.panelId = 0;
    this.panelName = '';

    if (this.searchFormGrp.valid) {
      this.isSearching = true;
      const session = this.searchFormGrp.value.sessionControl || '';
      const panel = this.searchFormGrp.value.panelControl || '';
      const reqBody = {
        "sessionNameId": session.id,
        "sessionId": session.id,
        "panelId": panel.id,
        "panelName": panel.name,
        "panelCategory": panel.panelCategory
      };

      this.getAvailableJustices(reqBody);
      this.getpanelJustices(reqBody);
      this.getPanelcounts(reqBody);
      this.panelId = panel.id;
      this.panelName = panel.name;
      this.isSearchComplete = true;
      this.searchFormGrp.disable(); // lock form when search complete

    }
    else {
      this.dialogService.openDialog1({
        data: {
          title: "Information",
          message: 'Please select Session and Panel',
          icon: 'info',
        }, disableClose: true,
        hasBackdrop: true,
        backdropClass: 'dialog-backdrop',
      });
    }
  }
  private getAvailableJustices(reqBody: any) {
    this.service.getAvailableJustices(reqBody).subscribe({
      next: (res: IResponse<IJudge[]>) => {
        if (res && Array.isArray(res.success)) {
          // ✅ Remove duplicates based on justiceId + justiceName
          const uniqueJudgesMap = new Map<string, IJudge>();

          res.success.forEach(judge => {
            const key = `${judge.justiceId}_${judge.justiceName}`;
            uniqueJudgesMap.set(key, judge);
          });

          this.availableJudges = Array.from(uniqueJudgesMap.values());
          this.filteredAvailableJudges = [...this.availableJudges];
          this.searchAvailableJudgesCtrl.setValue('');

          //console.log('Available Judges loaded successfully:', this.availableJudges);
        } else {
          //console.log('No judges data found.');
        }
      },
      error: (err) => {
        console.error('Error fetching court data:', err);
      },
    });
  }

  private getpanelJustices(reqBody: any) {
    this.service.getPanelJustices(reqBody).subscribe({
      next: (res: IResponse<IJudge[]>) => {
        if (res && Array.isArray(res.success)) {
          // Remove duplicates based on justiceId
          const uniqueJudgesMap = new Map<string, IJudge>();
          res.success.forEach(judge => {
            const key = `${judge.justiceId}_${judge.justiceName}`;
            uniqueJudgesMap.set(key, judge);
          });

          this.panelJudges = Array.from(uniqueJudgesMap.values());
          this.savedPanelJudges = this.panelJudges.map(j => ({ ...j }));
          //console.log('panel Judges loaded successfully:', this.panelJudges);
        } else {
          //console.log('No panel judges data found.');
        }
      },
      error: (err) => {
        console.error('Error fetching court data:', err);
      },
    });
  }
  private getPanelcounts(reqBody: any) {
    this.service.getPanelCounts(reqBody).subscribe({
      next: (res: IResponse<IJusticePanelCount[]>) => {
        if (res && Array.isArray(res.data)) {
          this.justicePanels = res.data;
          const panel = this.justicePanels.find(
            p => p.justicePanelId === this.panelId
          );
          this.panelCount = panel?.justiceCount ?? 0
          //console.log('panel Judges loaded successfully:', this.panelCount);
        } else {
          //console.log('No judges data found.');
        }
      },
      error: (err) => {
        console.error('Error fetching court data:', err);
      },
    });
  }


  resetHandler() {
    this.searchFormGrp.enable();
    this.searchFormGrp.reset();
    this.isSearchComplete = false;
    this.isSearching = false;
    this.availableJudges = [];
    this.filteredAvailableJudges = [];
    this.searchAvailableJudgesCtrl.setValue('');
    this.panelJudges = [];
    this.savedPanelJudges = [];
    this.hasChanges = false;
    this.panelId = 0;
    this.panelName = '';
  }

  saveAssignment() {
    if (this.panelJudges.length === 0) {
      this.dialogService.openDialog1({
        data: {
          title: "Warning",
          message: 'A panel must have at least one justice remaining.',
          icon: 'warning',
        }, disableClose: true,
        hasBackdrop: true,
        backdropClass: 'dialog-backdrop',
      });
      return;
    }

    // Simulate sessionId and panelId being grabbed from the forms
    const sessionId = this.searchFormGrp.value.sessionControl?.id || 1;
    const panelId = this.searchFormGrp.value.panelControl?.id || 1;

    const payload: any[] = [];

    // Deduplicate lists based on justiceId in case of drag & drop duplication issues
    const uniqueSaved = Array.from(new Map(this.savedPanelJudges.map(j => [j.justiceId, j])).values());
    const uniqueCurrent = Array.from(new Map(this.panelJudges.map(j => [j.justiceId, j])).values());

    // Process removed judges (they were in original unique saved, but not in current unique)
    uniqueSaved.forEach(originalJudge => {
      const isRetained = uniqueCurrent.some(j => j.justiceId === originalJudge.justiceId);
      if (!isRetained) {
        let dateString: string | null = null;
        if (originalJudge.beginDate) {
          const d = new Date(originalJudge.beginDate);
          dateString = d.toISOString().substring(0, 19);
        } else {
          dateString = "2015-11-24T04:30:00";
        }

        payload.push({
          id: originalJudge.id || null,
          panelId: panelId,
          sessionId: sessionId,
          justiceId: originalJudge.justiceId,
          justiceName: originalJudge.justiceName,
          beginDate: dateString,
          endDate: '',
          transType: "U"
        });
      }
    });

    // Process added judges (they are in current unique, but not in original unique saved)
    uniqueCurrent.forEach(currentJudge => {
      const isExisting = uniqueSaved.some(j => j.justiceId === currentJudge.justiceId);
      if (!isExisting) {
        let dateString: string | null = null;
        if (currentJudge.beginDate) {
          const d = new Date(currentJudge.beginDate);
          dateString = d.toISOString().substring(0, 19);
        } else {
          dateString = new Date().toISOString().substring(0, 19);
        }

        payload.push({
          id: currentJudge.id || null, // Preserve ID
          panelId: panelId,
          sessionId: sessionId,
          justiceId: currentJudge.justiceId,
          justiceName: currentJudge.justiceName,
          beginDate: dateString,
          endDate: null,
          transType: "I"
        });
      }
    });

    //console.log('Save Payload:', payload);

    this.service.savePanel(payload).subscribe({
      next: (res: any) => {
        if (res) {
          if (res.success) {
            const dialogRef = this.dialogService.openDialog1({
              data: {
                title: "Success",
                message: res.message || "Updated Successfully",
                icon: 'check_small',
              }, disableClose: true,
              hasBackdrop: true,
              backdropClass: 'dialog-backdrop',
            });
            // Auto reload data and restore state after success
            dialogRef.afterClosed().subscribe(() => {

            });

            this.searchHandler();
          } else {
            this.dialogService.openDialog1({
              data: {
                title: "Information",
                message: res.message || res.data?.validString || "An error occurred",
                icon: 'info',
              }, disableClose: true,
              hasBackdrop: true,
              backdropClass: 'dialog-backdrop',
            });
          }
        }
      },
      error: (err: any) => {
        this.dialogService.openDialog1({
          data: {
            title: "Error",
            message: "Failed to save data. Please try again.",
            icon: 'error',
          }, disableClose: true,
          hasBackdrop: true,
          backdropClass: 'dialog-backdrop',
        });
        console.error('Error saving data:', err);
      }
    });


  }

  protected filterSession() {
    if (!this.sessions) return;

    let search = this.sessionFilterCtrl.value;
    if (!search) {
      this.filteredSessions.next(this.sessions.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // Filter based on partyName
    this.filteredSessions.next(
      this.sessions.filter(session => session.name.toLowerCase().indexOf(search) > -1)
    );
  }
  protected filterPanels() {
    if (!this.panels) return;

    let search = this.panelsFilterCtrl.value;
    if (!search) {
      this.filteredPanels.next(this.panels.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // Filter based on partyName
    this.filteredPanels.next(
      this.panels.filter(panel => panel.name.toLowerCase().indexOf(search) > -1)
    );
  }

  protected filterAvailableJudges(search: string | null) {
    if (!this.availableJudges) return;
    if (!search) {
      this.filteredAvailableJudges = [...this.availableJudges];
      return;
    }
    const lowerSearch = search.toLowerCase();
    this.filteredAvailableJudges = this.availableJudges.filter(judge =>
      judge.justiceName.toLowerCase().includes(lowerSearch) ||
      judge.justiceId.toString().includes(lowerSearch)
    );
  }

}
